import * as zod from 'zod';

import type { CustomGripperTooltipSlots } from '@sb/integrations/implementations/CustomGripper/types/tooltipSlots';
import type { EquipmentItem } from '@sb/integrations/types';
import {
  optionBelongsToKind,
  tooltipSlotForOption,
} from '@sb/integrations/utils';
import type { TCPOffsetOption } from '@sb/motion-planning';
import { TCPOffsetOption as TCPOffsetOptionSchema } from '@sb/motion-planning';
import { ActionRequiredError, Step } from '@sb/remote-control/types';

/**
 * The selected tip's own name when the device names its tooltips — the option
 * resolves to a slot of `config.tooltips` — else `undefined` and the caller
 * falls back to the device name. An emptied slot names no tip, so a reference
 * to a deleted tooltip falls back too.
 */
function tipName(
  config: EquipmentItem['config'],
  tcpOption: TCPOffsetOption,
): string | undefined {
  if (!optionBelongsToKind(tcpOption, config.kind)) {
    return undefined;
  }

  const { tooltips } = config as { tooltips?: CustomGripperTooltipSlots };

  if (!tooltips) {
    return undefined;
  }

  const slot = tooltipSlotForOption(tcpOption);

  return slot === undefined ? undefined : tooltips[slot]?.name;
}

function describeTooltip(
  tcpOption: TCPOffsetOption,
  gripperID: string | undefined,
  equipment: EquipmentItem[] | undefined,
): string {
  if (tcpOption === 'wrist') {
    return 'Wrist flange';
  }

  if (tcpOption === 'wristCamera') {
    return 'Wrist camera';
  }

  // By id when the step pins a device; by the option's owning kind
  // otherwise — an id-less step is a supported state (e.g. a stale id
  // cleared on cross-robot import), and its option still names the kind.
  const owner = gripperID
    ? equipment?.find((item) => item.id === gripperID)
    : equipment?.find((item) =>
        optionBelongsToKind(tcpOption, item.config.kind),
      );

  if (!owner) {
    return 'the selected gripper';
  }

  return (
    tipName(owner.config, tcpOption) ??
    owner.config.name ??
    'the selected gripper'
  );
}

export namespace SetActiveTooltipStep {
  export const name = 'Set active tooltip';
  export const description = "Set the robot's Active Tooltip";
  export const librarySection = Step.LibrarySection.InterfaceWithMachines;
  export const featureFlag = 'setActiveTooltip' as const;
  export const argumentKind = 'SetActiveTooltip';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    tcpOption: TCPOffsetOptionSchema.optional(),
    gripperID: zod.string().optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    if (!args.tcpOption) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Select a tooltip',
        fieldId: 'tcpOption',
      });
    }

    return {
      ...stepData,
      stepKind: 'SetActiveTooltip',
      args: {
        tcpOption: args.tcpOption,
        gripperID: args.gripperID,
      },
    };
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    includeStepName,
    equipment,
  }) => {
    if (args?.argumentKind !== argumentKind || !args.tcpOption) {
      return null;
    }

    const tooltipName = describeTooltip(
      args.tcpOption,
      args.gripperID,
      equipment,
    );

    return includeStepName
      ? `Set active tooltip to ${tooltipName}`
      : `Sets active tooltip to ${tooltipName}`;
  };
}

SetActiveTooltipStep satisfies Step.StepKindInfo;
